import Fuse from "fuse.js"

import { contentTokens, normalizeForSearch } from "@/lib/search/normalize"

export function tokenize(s: string): string[] {
  const norm = normalizeForSearch(s)
  if (!norm) return []
  return norm.split(" ").filter(Boolean)
}

export type SearchableFaq = {
  slug: string
  category: string
  question: string
  answer: string // already plain text (HTML stripped)
}

export type SearchResult = SearchableFaq & {
  score: number // lower = better
  matchedIn: "question" | "answer"
}

// Returns a sub-score in [0, 0.49] based on how early phrase appears in haystack.
// Earlier position = lower sub-score = better rank. Stays within the 0.5-width band.
function positionSubScore(haystack: string, phrase: string): number {
  const idx = haystack.indexOf(phrase)
  if (idx === -1 || haystack.length <= phrase.length) return 0
  return (idx / (haystack.length - phrase.length)) * 0.49
}

export function searchFaq(
  query: string,
  items: SearchableFaq[]
): SearchResult[] {
  const tokens = tokenize(query)
  if (tokens.length === 0) return []
  const phrase = normalizeForSearch(query)

  const results: SearchResult[] = []
  const matchedSlugs = new Set<string>()
  for (const item of items) {
    const nq = normalizeForSearch(item.question)
    const na = normalizeForSearch(item.answer)
    const haystack = `${nq} ${na}`
    const allMatch = tokens.every((t) => haystack.includes(t))
    if (!allMatch) continue

    const inQuestion = tokens.every((t) => nq.includes(t))
    const phraseInQuestion = nq.includes(phrase)
    const phraseInAnswer = na.includes(phrase)

    // Lower score = better. Bands:
    //  0.0–0.49 — exact phrase in question (sub-scored by position)
    //  0.5–0.99 — all tokens in question (split)
    //  1.0–1.49 — exact phrase in answer
    //  1.5–1.99 — all tokens in answer (split)
    let score: number
    let matchedIn: "question" | "answer"
    if (phraseInQuestion) {
      score = 0 + positionSubScore(nq, phrase)
      matchedIn = "question"
    } else if (inQuestion) {
      score = 0.5
      matchedIn = "question"
    } else if (phraseInAnswer) {
      score = 1
      matchedIn = "answer"
    } else {
      score = 1.5
      matchedIn = "answer"
    }
    results.push({ ...item, score, matchedIn })
    matchedSlugs.add(item.slug)
  }

  // Partial fallback: strict AND found nothing, but the question carried real
  // content words. Rank by the FRACTION of content tokens matched, so the
  // closest answer still comes first — degrading instead of dying. Band 1.5–2.0
  // keeps every exact hit above these. Only runs when strict AND is empty, so
  // it can never reorder or dilute an exact result set.
  if (results.length === 0) {
    const content = contentTokens(tokens)
    // Gate on the RAW token count, not the content count: "where is the
    // questionnaire" leaves a single content token once the filler is dropped,
    // and that case is exactly the dead end this tier exists for. A genuinely
    // single-word query (tokens.length === 1) is excluded and falls to the
    // fuzzy tier below, where it belongs.
    if (tokens.length > 1 && content.length > 0) {
      for (const item of items) {
        const nq = normalizeForSearch(item.question)
        const na = normalizeForSearch(item.answer)
        const haystack = `${nq} ${na}`
        const hits = content.filter((t) => haystack.includes(t))
        if (hits.length === 0) continue
        const inQuestion = hits.filter((t) => nq.includes(t)).length
        // 1.5 (every content token matched) → just under 2.0 (one of many).
        const coverage = hits.length / content.length
        results.push({
          ...item,
          score: 2 - 0.5 * coverage,
          matchedIn: inQuestion >= hits.length / 2 ? "question" : "answer",
        })
        matchedSlugs.add(item.slug)
      }
    }
  }

  // Fuzzy fallback: only when nothing above matched, the query is a single
  // token (a multi-token miss is the partial tier's job, not Fuse's), and the
  // phrase is non-trivial (>= 3 chars). Score band starts at 2.0 so exact
  // hits always rank first.
  if (results.length === 0 && tokens.length === 1 && phrase.length >= 3) {
    const indexed = items.map((i) => ({
      ...i,
      _nq: normalizeForSearch(i.question),
      _na: normalizeForSearch(i.answer),
    }))
    const fuse = new Fuse(indexed, {
      keys: [
        { name: "_nq", weight: 2 },
        { name: "_na", weight: 1 },
      ],
      threshold: 0.4, // 0.32 misses transpositions (mir→mri scores 0.33); 0.4 catches typos, gibberish still rejected
      ignoreLocation: true,
      minMatchCharLength: 2,
      includeScore: true,
    })
    const fuzzy = fuse.search(phrase)
    for (const f of fuzzy) {
      const fScore = f.score ?? 1
      results.push({
        slug: f.item.slug,
        category: f.item.category,
        question: f.item.question,
        answer: f.item.answer,
        score: 2 + fScore, // 2.0 - 3.0 band
        matchedIn: f.item._nq.includes(phrase) ? "question" : "answer",
      })
    }
  }

  results.sort((a, b) => a.score - b.score)
  return results
}
